import React, { useState, useEffect } from 'react';
import api from '../utils/api';
import { useNavigate, Link } from 'react-router-dom';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import { Bar } from 'react-chartjs-2';
import PipelineStatus from '../components/PipelineStatus';

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
);

const ITEMS_PER_PAGE = 10;

export default function ObserverDashboard() {
    const [transactions, setTransactions] = useState([]);
    const [summary, setSummary] = useState(null);
    const [loading, setLoading] = useState(true);
    const [type, setType] = useState('all');
    const [fromDate, setFromDate] = useState('');
    const [toDate, setToDate] = useState('');
    const [activeTab, setActiveTab] = useState('audit'); // 'audit', 'cross-border', 'generation-overview'
    const [crossBorderData, setCrossBorderData] = useState([]);
    const [generationOverview, setGenerationOverview] = useState({ plants: [], chartData: [] });
    const [cbSummary, setCbSummary] = useState({ total_usd: 0, by_country: [] });
    const [cbCountryFilter, setCbCountryFilter] = useState('all');
    const [page, setPage] = useState(1);
    const [totalTransactions, setTotalTransactions] = useState(0);
    
    // Theme state
    const [theme, setTheme] = useState(localStorage.getItem('observer-theme') || 'dark');
    const [expandedPlantId, setExpandedPlantId] = useState(null);
    const navigate = useNavigate();

    const fetchSummary = React.useCallback(async () => {
        try {
            const res = await api.get('/api/observer/summary');
            setSummary(res.data);
        } catch (err) { console.error('Summary fetch failed', err); }
    }, []);

    const fetchTransactions = React.useCallback(async () => {
        setLoading(true);
        try {
            const params = { page, limit: ITEMS_PER_PAGE };
            if (type !== 'all') params.type = type;
            if (fromDate) params.fromDate = fromDate;
            if (toDate) params.toDate = toDate;

            const res = await api.get('/api/observer/transactions', { params });
            setTransactions(res.data.data || []);
            setTotalTransactions(res.data.total || 0);
            setLoading(false);
        } catch (err) {
            console.error('Transactions fetch failed', err);
            setLoading(false);
        }
    }, [page, type, fromDate, toDate]);

    const fetchCrossBorder = React.useCallback(async () => {
        try {
            const params = { fromDate, toDate, country: cbCountryFilter };
            const [dataRes, summaryRes] = await Promise.all([
                api.get('/api/observer/cross-border-retirements', { params }),
                api.get('/api/observer/cross-border-summary', { params })
            ]);
            setCrossBorderData(dataRes.data);
            setCbSummary(summaryRes.data);
        } catch (err) { console.error('Cross-border fetch failed', err); }
    }, [fromDate, toDate, cbCountryFilter]);

    const fetchGenerationOverview = React.useCallback(async () => {
        try {
            const res = await api.get('/api/observer/generation-overview');
            setGenerationOverview(res.data);
        } catch (err) { console.error('Generation overview fetch failed', err); }
    }, []);

    useEffect(() => {
        fetchSummary();
        fetchGenerationOverview(); // Always fetch plants for name mapping
        
        if (activeTab === 'audit') fetchTransactions();
        else if (activeTab === 'cross-border') fetchCrossBorder();
        
        // Refresh intervals
        const sumInterval = setInterval(fetchSummary, 30000);
        const dataInterval = setInterval(() => {
            if (activeTab === 'audit') fetchTransactions();
            else if (activeTab === 'cross-border') fetchCrossBorder();
            else fetchGenerationOverview();
        }, 30000);

        return () => {
            clearInterval(sumInterval);
            clearInterval(dataInterval);
        };
    }, [fetchSummary, fetchTransactions, fetchCrossBorder, fetchGenerationOverview, activeTab]);

    const chartData = {
        labels: ['Certificates', 'Retirements', 'Users'],
        datasets: [{
            label: 'Total Volume',
            data: [
                summary?.totalAttestations || 0,
                summary?.totalRetired || 0,
                summary?.usersByRole ? Object.values(summary.usersByRole).reduce((a,b)=>a+b, 0) : 0
            ],
            backgroundColor: [
                'rgba(99, 102, 241, 0.8)',
                'rgba(16, 185, 129, 0.8)',
                'rgba(244, 63, 94, 0.8)'
            ],
            borderRadius: 12,
        }]
    };

    const generationChartData = {
        labels: generationOverview.chartData.map(d => d.date),
        datasets: [{
            label: 'Issued Certificates',
            data: generationOverview.chartData.map(d => d.count),
            backgroundColor: 'rgba(16, 185, 129, 0.8)',
            borderRadius: 6,
        }]
    };

    const handleExport = () => {
        const url = `/api/observer/export/csv?fromDate=${fromDate}&toDate=${toDate}`;
        api.get(url, { responseType: 'blob' }).then(res => {
            const blob = new Blob([res.data], { type: 'text/csv' });
            const link = document.createElement('a');
            link.href = window.URL.createObjectURL(blob);
            link.download = `audit_export_${new Date().toISOString().split('T')[0]}.csv`;
            link.click();
        });
    };

    const toggleTheme = () => {
        const newTheme = theme === 'dark' ? 'light' : 'dark';
        setTheme(newTheme);
        localStorage.setItem('observer-theme', newTheme);
    };

    const handleLogout = () => {
        localStorage.clear();
        navigate('/login');
    };

    const totalPages = Math.ceil(totalTransactions / ITEMS_PER_PAGE);

    return (
        <div className={`min-h-screen transition-colors duration-500 ${theme === 'dark' ? 'bg-[#080a0e] text-slate-200' : 'bg-slate-50 text-slate-600'} p-4 sm:p-8 font-sans`}>
            {/* Header */}
            <header className="flex flex-col md:flex-row justify-between items-start md:items-center gap-6 mb-10 max-w-7xl mx-auto">
                <Link to="/" className="flex items-center gap-4 group transition-all">
                    <div>
                        <div className="flex items-center gap-3">
                            <div className="w-10 h-10 flex items-center justify-center flex-shrink-0">
                                <svg className="w-8 h-8 text-emerald-400" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                    <path d="M2 20L8 8L13 16" />
                                    <path d="M8 20L15 6L22 20" />
                                    <path d="M8 8L15 6" strokeDasharray="1.5 1.5" />
                                    <circle cx="15" cy="6" r="1.5" fill="currentColor" />
                                </svg>
                            </div>
                            <h1 className={`text-3xl font-bold tracking-tight ${theme === 'dark' ? 'text-white' : 'text-slate-900'}`}>Observer <span className="text-indigo-500 italic">Audit Terminal</span></h1>
                        </div>
                        <p className="text-slate-500 text-[10px] mt-1 ml-13 uppercase tracking-[0.3em] font-black group-hover:text-indigo-500 transition-colors">Regulatory Monitoring Interface</p>
                    </div>
                </Link>
                <div className="flex flex-wrap gap-4 w-full md:w-auto">
                    <button 
                        onClick={toggleTheme}
                        className={`p-3 rounded-2xl border transition-all flex items-center justify-center gap-2 group flex-1 sm:flex-initial ${theme === 'dark' ? 'bg-white/5 border-white/10 text-yellow-400 hover:bg-white/10' : 'bg-slate-200/50 border-slate-200 text-slate-800 hover:bg-slate-200'}`}
                    >
                        {theme === 'dark' ? (
                            <svg className="w-5 h-5 group-hover:rotate-12 transition-transform" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M10 2a1 1 0 011 1v1a1 1 0 11-2 0V3a1 1 0 011-1zm4 8a4 4 0 11-8 0 4 4 0 018 0zm-.464 4.95l.707.707a1 1 0 001.414-1.414l-.707-.707a1 1 0 00-1.414 1.414zm2.12-10.607a1 1 0 010 1.414l-.706.707a1 1 0 11-1.414-1.414l.707-.707a1 1 0 011.414 0zM17 11a1 1 0 100-2h-1a1 1 0 100 2h1zm-7 4a1 1 0 011 1v1a1 1 0 11-2 0v-1a1 1 0 011-1zM5.05 6.464A1 1 0 106.465 5.05l-.708-.707a1 1 0 00-1.414 1.414l.707.707zm1.414 8.486l-.707.707a1 1 0 01-1.414-1.414l.707-.707a1 1 0 011.414 1.414zM4 11a1 1 0 100-2H3a1 1 0 000 2h1z" clipRule="evenodd"></path></svg>
                        ) : (
                            <svg className="w-5 h-5 group-hover:-rotate-12 transition-transform" fill="currentColor" viewBox="0 0 20 20"><path d="M17.293 13.293A8 8 0 016.707 2.707a8.001 8.001 0 1010.586 10.586z"></path></svg>
                        )}
                        <span className="text-[10px] font-black uppercase tracking-widest">{theme === 'dark' ? 'Light Mode' : 'Dark Mode'}</span>
                    </button>
                    <button onClick={handleLogout} className={`px-5 py-2.5 bg-white/5 hover:bg-white/10 text-slate-400 border rounded-2xl transition font-bold text-xs uppercase tracking-widest flex-1 sm:flex-initial ${theme === 'dark' ? 'border-white/10 shadow-none' : 'border-slate-200 shadow-sm'}`}>Logout</button>
                </div>
            </header>

            {/* Tab Navigation */}
            <div className="max-w-7xl mx-auto mb-8 flex flex-wrap gap-4">
                <button 
                    onClick={() => setActiveTab('audit')}
                    className={`px-8 py-4 rounded-2xl font-black text-[10px] uppercase tracking-widest transition-all ${activeTab === 'audit' ? 'bg-indigo-600 text-white shadow-lg shadow-indigo-600/20' : 'bg-white/5 text-slate-500 hover:bg-white/10'}`}
                >
                    Ledger Audit Trail
                </button>
                <button 
                    onClick={() => setActiveTab('cross-border')}
                    className={`px-8 py-4 rounded-2xl font-black text-[10px] uppercase tracking-widest transition-all ${activeTab === 'cross-border' ? 'bg-emerald-600 text-white shadow-lg shadow-emerald-600/20' : 'bg-white/5 text-slate-500 hover:bg-white/10'}`}
                >
                    Capital Flow Tracking
                </button>
                <button 
                    onClick={() => setActiveTab('generation-overview')}
                    className={`px-8 py-4 rounded-2xl font-black text-[10px] uppercase tracking-widest transition-all ${activeTab === 'generation-overview' ? 'bg-cyan-600 text-white shadow-lg shadow-cyan-600/20' : 'bg-white/5 text-slate-500 hover:bg-white/10'}`}
                >
                    Plant & Generation
                </button>
            </div>

            <main className="max-w-7xl mx-auto space-y-10 border-t-8 border-indigo-500">
                {activeTab === 'audit' ? (
                    <>
                        {/* Summary Grid */}
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 p-8">
                            <div className={`p-6 rounded-3xl border transition-all group col-span-1 md:col-span-2 ${theme === 'dark' ? 'glass border-indigo-500/30 bg-indigo-500/5 hover:border-indigo-500/50' : 'bg-white border-indigo-100 shadow-xl hover:border-indigo-300'}`}>
                                <h4 className="text-indigo-400 text-[10px] font-black uppercase tracking-widest mb-2 flex items-center gap-2">
                                    <span className="w-2 h-2 bg-indigo-500 rounded-full animate-pulse"></span>
                                    Platform Asset Value (USD)
                                </h4>
                                <div className={`text-5xl font-black tracking-tighter group-hover:scale-105 transition-transform origin-left ${theme === 'dark' ? 'text-white' : 'text-slate-900'}`}>
                                    ${Number(summary?.totalAssetValueUSD || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}
                                </div>
                                <p className="text-[10px] text-slate-500 uppercase font-black mt-2">Total value of active and retired I-REC certificates on the ledger</p>
                            </div>

                            <div className={`p-6 rounded-3xl border transition-all group col-span-1 md:col-span-2 ${theme === 'dark' ? 'glass border-cyan-500/30 bg-cyan-500/5 hover:border-cyan-500/50' : 'bg-white border-cyan-100 shadow-xl hover:border-cyan-300'}`}>
                                <h4 className="text-cyan-400 text-[10px] font-black uppercase tracking-widest mb-2">
                                    GCC Registration
                                </h4>
                                <div className={`text-4xl font-black tracking-tighter group-hover:scale-105 transition-transform origin-left ${theme === 'dark' ? 'text-white' : 'text-slate-900'}`}>
                                    {summary?.gccApprovedPlants || 0}
                                    <span className="text-lg text-slate-500 ml-1">
                                        / {summary?.irecEligiblePlants || 0}
                                    </span>
                                </div>
                                <p className="text-[10px] text-slate-500 uppercase font-black mt-2">
                                    Plants Approved / Eligible
                                </p>
                            </div>

                            <div className={`p-6 rounded-3xl border transition-all group ${theme === 'dark' ? 'glass border-white/5 hover:border-indigo-500/20' : 'bg-white border-slate-200 shadow-md hover:border-indigo-300 text-slate-800'}`}>
                                <h4 className="text-slate-500 text-[10px] font-black uppercase tracking-widest mb-2">Total Attestations</h4>
                                <div className="text-4xl font-black text-indigo-400 tracking-tighter group-hover:scale-105 transition-transform origin-left">{summary?.totalAttestations || 0}</div>
                            </div>

                            <div className={`p-6 rounded-3xl border transition-all group ${theme === 'dark' ? 'glass border-white/5 hover:border-emerald-500/20' : 'bg-white border-slate-200 shadow-md hover:border-emerald-300 text-slate-800'}`}>
                                <h4 className="text-slate-500 text-[10px] font-black uppercase tracking-widest mb-2">Energy (MWh)</h4>
                                <div className="text-4xl font-black text-emerald-500 tracking-tighter group-hover:scale-105 transition-transform origin-left">{summary?.totalMwh || '0.00'}</div>
                            </div>

                            <div className={`p-6 rounded-3xl border transition-all group ${theme === 'dark' ? 'glass border-white/5 hover:border-rose-500/20' : 'bg-white border-slate-200 shadow-md hover:border-rose-300 text-slate-800'}`}>
                                <h4 className="text-slate-500 text-[10px] font-black uppercase tracking-widest mb-2">Retired Units</h4>
                                <div className="text-4xl font-black text-rose-500 tracking-tighter group-hover:scale-105 transition-transform origin-left">{summary?.totalRetired || 0}</div>
                            </div>

                            <div className={`p-6 rounded-3xl border transition-all group ${theme === 'dark' ? 'glass border-emerald-500/30 bg-emerald-500/5 hover:border-emerald-500/50' : 'bg-white border-emerald-100 shadow-md hover:border-emerald-300 text-slate-800'}`}>
                                <h4 className="text-emerald-400 text-[10px] font-black uppercase tracking-widest mb-2">
                                    Total I-RECs Issued
                                </h4>
                                <div className="text-4xl font-black text-emerald-400 tracking-tighter group-hover:scale-105 transition-transform origin-left">
                                    {summary?.totalIrecsIssued || 0}
                                </div>
                                <p className="text-[10px] text-slate-500 uppercase font-black mt-2">
                                    Via GCC · Evident Registry
                                </p>
                            </div>
                        </div>

                        {/* Activity Chart Area */}
                        <div className={`p-8 border-t ${theme === 'dark' ? 'border-white/5' : 'border-slate-200'}`}>
                            <h3 className="text-slate-500 text-[10px] font-black uppercase tracking-widest mb-6">Activity Distribution Analysis</h3>
                            <div className={`h-64 p-8 rounded-3xl border ${theme === 'dark' ? 'glass border-white/5' : 'bg-white border-slate-200 shadow-inner'}`}>
                                <Bar 
                                    data={chartData} 
                                    options={{
                                        responsive: true,
                                        maintainAspectRatio: false,
                                        plugins: { legend: { display: false } },
                                        scales: {
                                            y: { grid: { color: 'rgba(255,255,255,0.05)' }, ticks: { color: '#64748b' } },
                                            x: { grid: { display: false }, ticks: { color: '#64748b' } }
                                        }
                                    }} 
                                />
                            </div>
                        </div>

                        {/* Audit Controls */}
                        <div className={`${theme === 'dark' ? 'bg-white/5 border-white/5' : 'bg-white border-slate-200'} p-6 rounded-[2.5rem] border flex flex-wrap items-center gap-6 shadow-2xl`}>
                            <div className="flex items-center gap-4">
                                <label className="text-[10px] font-black uppercase tracking-widest text-slate-500">Filters:</label>
                                <select 
                                    value={type} 
                                    onChange={e => { setType(e.target.value); setPage(1); }}
                                    className={`${theme === 'dark' ? 'bg-[#0d1117] border-white/10' : 'bg-slate-50 border-slate-200'} border rounded-xl px-4 py-2 text-xs font-bold focus:outline-none focus:border-indigo-500`}
                                >
                                    <option value="all">All Transactions</option>
                                    <option value="energy">Energy Attestations</option>
                                    <option value="purchase">Certificate Purchases</option>
                                    <option value="retirement">Retirements</option>
                                    <option value="user">User Events</option>
                                </select>
                            </div>

                            <div className="flex items-center gap-4">
                                <input 
                                    type="date" 
                                    value={fromDate}
                                    onChange={e => setFromDate(e.target.value)}
                                    className={`${theme === 'dark' ? 'bg-[#0d1117] border-white/10' : 'bg-slate-50 border-slate-200'} border rounded-xl px-4 py-2 text-xs font-bold focus:outline-none focus:border-indigo-500`}
                                />
                                <span className="text-slate-500 text-xs">to</span>
                                <input 
                                    type="date" 
                                    value={toDate}
                                    onChange={e => setToDate(e.target.value)}
                                    className={`${theme === 'dark' ? 'bg-[#0d1117] border-white/10' : 'bg-slate-50 border-slate-200'} border rounded-xl px-4 py-2 text-xs font-bold focus:outline-none focus:border-indigo-500`}
                                />
                            </div>

                            <button 
                                onClick={handleExport}
                                className="ml-auto flex items-center gap-3 px-6 py-3 bg-indigo-600 hover:bg-indigo-500 text-white rounded-2xl text-[10px] font-black uppercase tracking-widest transition shadow-lg shadow-indigo-500/20"
                            >
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"></path></svg>
                                Export CSV
                            </button>
                        </div>

                        {/* Audit Table */}
                        <div className={`${theme === 'dark' ? 'bg-white/5 border-white/5' : 'bg-white border-slate-200'} rounded-[2.5rem] border overflow-hidden shadow-2xl transition-all`}>
                            <div className="overflow-x-auto">
                                <table className="w-full text-left border-collapse">
                                    <thead>
                                        <tr className={`${theme === 'dark' ? 'bg-white/5' : 'bg-slate-100'} border-b ${theme === 'dark' ? 'border-white/5' : 'border-slate-200'}`}>
                                            <th className="px-8 py-6 text-[10px] font-black uppercase tracking-widest text-slate-500">Timestamp</th>
                                            <th className="px-8 py-6 text-[10px] font-black uppercase tracking-widest text-slate-500">Event Type</th>
                                            <th className="px-8 py-6 text-[10px] font-black uppercase tracking-widest text-slate-500">Source Plant</th>
                                            <th className="px-8 py-6 text-[10px] font-black uppercase tracking-widest text-slate-500">Subject</th>
                                            <th className="px-8 py-6 text-[10px] font-black uppercase tracking-widest text-slate-500">Details</th>
                                            <th className="px-8 py-6 text-[10px] font-black uppercase tracking-widest text-slate-500">Status</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-white/5">
                                        {loading ? (
                                            <tr><td colSpan="6" className="p-20 text-center animate-pulse text-indigo-500 font-black uppercase tracking-widest">Hydrating Audit Trail...</td></tr>
                                        ) : transactions.map((tx, idx) => (
                                            <tr key={idx} className={`${theme === 'dark' ? 'hover:bg-white/5' : 'hover:bg-slate-50'} transition-colors`}>
                                                <td className="px-8 py-6 font-mono text-[10px] text-slate-500 whitespace-nowrap">{new Date(tx.timestamp).toLocaleString()}</td>
                                                <td className="px-8 py-6">
                                                    <span className={`px-3 py-1 rounded-full text-[9px] font-black uppercase tracking-widest border ${
                                                        tx.type === 'attestation' ? 'bg-indigo-500/10 text-indigo-400 border-indigo-500/20' :
                                                        tx.type === 'retirement' ? 'bg-rose-500/10 text-rose-400 border-rose-500/20' :
                                                        tx.type === 'purchase' ? 'bg-cyan-500/10 text-cyan-400 border-cyan-500/20' :
                                                        'bg-blue-500/10 text-blue-400 border-blue-500/20'
                                                    }`}>
                                                        {tx.type}
                                                    </span>
                                                </td>
                                                <td className="px-8 py-6 text-xs font-bold text-cyan-400">
                                                    {tx.plantName || (generationOverview.plants.find(p => String(p.plant_id) === String(tx.plantId))?.plant_name) || 'Global System'}
                                                </td>
                                                <td className="px-8 py-6 font-bold text-xs">
                                                    {tx.type === 'user' ? tx.user_email : tx.attestationId}
                                                </td>
                                                <td className="px-8 py-6 text-xs text-slate-500 truncate max-w-md">
                                                    {tx.type === 'user' 
                                                        ? `Action: ${tx.action} | ${JSON.stringify(tx.details)}`
                                                        : tx.type === 'retirement' 
                                                        ? `ESG Purpose: ${tx.details?.notes || 'N/A'}`
                                                        : tx.type === 'purchase'
                                                        ? `MWh: ${tx.details?.mwh || '—'} | Purpose: ${tx.details?.purpose || '—'}`
                                                        : `Plant: ${tx.details?.plantId || '—'} | MWh: ${tx.details?.mwh || tx.details?.docType || '—'}`
                                                    }
                                                </td>
                                                <td className="px-8 py-6">
                                                    <span className={`text-[10px] font-black uppercase tracking-widest ${
                                                        tx.status === 'active' ? 'text-emerald-500' :
                                                        tx.status === 'retired' ? 'text-blue-500' :
                                                        tx.status === 'rejected' ? 'text-rose-500' : 
                                                        tx.status === 'pending_expert' || tx.status === 'pending' ? 'text-amber-500' : 'text-slate-500'
                                                    }`}>
                                                        {tx.status || '--'}
                                                    </span>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                            
                            {/* Pagination controls for Audit Trail */}
                            {totalPages > 1 && (
                                <div className={`p-6 ${theme === 'dark' ? 'bg-white/5' : 'bg-slate-50'} border-t ${theme === 'dark' ? 'border-white/5' : 'border-slate-100'} flex justify-center items-center gap-6`}>
                                    <button onClick={() => setPage(p => Math.max(1, p-1))} disabled={page === 1} className="p-2 hover:bg-indigo-500/10 disabled:opacity-30 transition rounded-lg text-indigo-500">
                                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M15 19l-7-7 7-7"></path></svg>
                                    </button>
                                    <span className="text-[10px] font-black uppercase tracking-widest text-slate-500">Page {page} <span className="mx-2 opacity-30">/</span> {totalPages}</span>
                                    <button onClick={() => setPage(p => Math.min(totalPages, p+1))} disabled={page === totalPages} className="p-2 hover:bg-indigo-500/10 disabled:opacity-30 transition rounded-lg text-indigo-500">
                                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M9 5l7 7-7 7"></path></svg>
                                    </button>
                                </div>
                            )}
                        </div>
                    </>
                ) : activeTab === 'cross-border' ? (
                    <>
                        {/* Cross-Border Summary Grid */}
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 p-8">
                            <div className={`p-6 rounded-3xl border transition-all group ${theme === 'dark' ? 'glass border-white/5 hover:border-emerald-500/20' : 'bg-white border-slate-200 shadow-md hover:border-emerald-300 text-slate-800'}`}>
                                <h4 className="text-slate-500 text-[10px] font-black uppercase tracking-widest mb-2">Total Cross-Border Value (USD)</h4>
                                <div className="text-4xl font-black text-emerald-500 tracking-tighter group-hover:scale-105 transition-transform origin-left">
                                    ${cbSummary.total_usd?.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                                </div>
                            </div>
                            {cbSummary.by_country.slice(0, 2).map((c, idx) => (
                                <div key={idx} className={`p-6 rounded-3xl border transition-all group ${theme === 'dark' ? 'glass border-white/5 hover:border-indigo-500/20' : 'bg-white border-slate-200 shadow-md hover:border-indigo-300 text-slate-800'}`}>
                                    <h4 className="text-slate-500 text-[10px] font-black uppercase tracking-widest mb-2">Top Country: {c.country}</h4>
                                    <div className="text-4xl font-black text-indigo-400 tracking-tighter group-hover:scale-105 transition-transform origin-left">
                                        ${c.total_usd?.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                                    </div>
                                    <p className="text-[9px] text-slate-500 uppercase font-black mt-2">{c.count} Retirements</p>
                                </div>
                            ))}
                            <div className={`p-6 rounded-3xl border transition-all group ${theme === 'dark' ? 'glass border-indigo-500/30 bg-indigo-500/5 hover:border-indigo-500/50' : 'bg-white border-slate-200 shadow-md hover:border-indigo-300 text-slate-800'}`}>
                                <h4 className="text-indigo-400 text-[10px] font-black uppercase tracking-widest mb-2">
                                    Registry Standard
                                </h4>
                                <div className={`text-lg font-black mt-2 ${theme === 'dark' ? 'text-white' : 'text-slate-850'}`}>
                                    I-REC(E)
                                </div>
                                <p className="text-[10px] text-slate-500 mt-2">
                                    GCC Issuer · Evident Registry
                                </p>
                                <p className="text-[10px] text-slate-500">
                                    Nepal · Bagmati Province
                                </p>
                            </div>
                        </div>

                        {/* Cross-Border Controls */}
                        <div className={`p-6 rounded-[2.5rem] border flex flex-wrap items-center gap-6 mb-10 shadow-2xl ${theme === 'dark' ? 'glass border-white/5' : 'bg-white border-slate-200 text-slate-800'}`}>
                            <div className="flex items-center gap-4">
                                <label className="text-[10px] font-black uppercase tracking-widest text-slate-500">Country:</label>
                                <select 
                                    value={cbCountryFilter} 
                                    onChange={e => setCbCountryFilter(e.target.value)}
                                    className={`border rounded-xl px-4 py-2 text-xs font-bold focus:outline-none focus:border-emerald-500 ${theme === 'dark' ? 'bg-[#0d1117] border-white/10 text-white' : 'bg-white border-slate-300 text-slate-800'}`}
                                >
                                    <option value="all">All Countries</option>
                                    <option value="US">USA (US)</option>
                                    <option value="JP">Japan (JP)</option>
                                    <option value="GB">UK (GB)</option>
                                    <option value="DE">Germany (DE)</option>
                                    <option value="NP">Nepal (NP)</option>
                                </select>
                            </div>
                            <div className="flex items-center gap-4">
                                <input 
                                    type="date" 
                                    value={fromDate}
                                    onChange={e => setFromDate(e.target.value)}
                                    className={`border rounded-xl px-4 py-2 text-xs font-bold focus:outline-none focus:border-emerald-500 ${theme === 'dark' ? 'bg-[#0d1117] border-white/10 text-white' : 'bg-white border-slate-300 text-slate-800'}`}
                                />
                                <span className={`text-xs ${theme === 'dark' ? 'text-white' : 'text-slate-500'}`}>to</span>
                                <input 
                                    type="date" 
                                    value={toDate}
                                    onChange={e => setToDate(e.target.value)}
                                    className={`border rounded-xl px-4 py-2 text-xs font-bold focus:outline-none focus:border-emerald-500 ${theme === 'dark' ? 'bg-[#0d1117] border-white/10 text-white' : 'bg-white border-slate-300 text-slate-800'}`}
                                />
                            </div>
                        </div>

                        {/* Cross-Border Table */}
                        <div className={`rounded-[2.5rem] border overflow-hidden shadow-2xl ${theme === 'dark' ? 'glass border-white/5' : 'bg-white border-slate-200'}`}>
                            <div className="overflow-x-auto">
                                <table className="w-full text-left border-collapse">
                                    <thead>
                                        <tr className={`border-b ${theme === 'dark' ? 'bg-white/5 border-white/5' : 'bg-slate-100 border-slate-200'}`}>
                                            <th className="px-8 py-6 text-[10px] font-black uppercase tracking-widest text-slate-500">Retirement ID</th>
                                            <th className="px-8 py-6 text-[10px] font-black uppercase tracking-widest text-slate-500">Investor (HIDDEN)</th>
                                            <th className="px-8 py-6 text-[10px] font-black uppercase tracking-widest text-slate-500">Country</th>
                                            <th className="px-8 py-6 text-[10px] font-black uppercase tracking-widest text-slate-500">Asset Value (USD)</th>
                                            <th className="px-8 py-6 text-[10px] font-black uppercase tracking-widest text-slate-500">Timestamp</th>
                                        </tr>
                                    </thead>
                                    <tbody className={`divide-y ${theme === 'dark' ? 'divide-white/5' : 'divide-slate-100'}`}>
                                        {crossBorderData.map((r, idx) => (
                                            <tr key={idx} className={`transition-colors ${theme === 'dark' ? 'hover:bg-white/5' : 'hover:bg-slate-50'}`}>
                                                <td className="px-8 py-6 font-mono text-[10px] text-indigo-400">RET-{r.id}</td>
                                                <td className={`px-8 py-6 text-xs ${theme === 'dark' ? 'text-slate-400' : 'text-slate-600'}`}>{r.investor_email.replace(/(.{3})(.*)(@.*)/, "$1***$3")}</td>
                                                <td className={`px-8 py-6 font-black text-xs ${theme === 'dark' ? 'text-white' : 'text-slate-800'}`}>{r.investor_country}</td>
                                                <td className="px-8 py-6 font-black text-xs text-emerald-500">${r.asset_value}</td>
                                                <td className={`px-8 py-6 text-xs ${theme === 'dark' ? 'text-slate-500' : 'text-slate-600'}`}>{new Date(r.retired_at).toLocaleString()}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </>
                ) : (
                    <>
                        <div className="p-8 border-b border-white/5">
                            <h3 className="text-slate-500 text-[10px] font-black uppercase tracking-widest mb-6">Total Issued Certificates (Last 30 Days)</h3>
                            <div className={`h-64 p-8 rounded-3xl border ${theme === 'dark' ? 'glass border-white/5' : 'bg-white border-slate-200 shadow-inner'}`}>
                                <Bar 
                                    data={generationChartData} 
                                    options={{
                                        responsive: true,
                                        maintainAspectRatio: false,
                                        plugins: { legend: { display: false } },
                                        scales: {
                                            y: { grid: { color: 'rgba(255,255,255,0.05)' }, ticks: { color: '#64748b' }, beginAtZero: true },
                                            x: { grid: { display: false }, ticks: { color: '#64748b' } }
                                        }
                                    }} 
                                />
                            </div>
                        </div>

                        <div className={`rounded-[2.5rem] border overflow-hidden shadow-2xl mt-8 ${theme === 'dark' ? 'glass border-white/5' : 'bg-white border-slate-200'}`}>
                            <div className={`p-8 border-b ${theme === 'dark' ? 'border-white/5' : 'border-slate-200 bg-slate-50'}`}>
                                <h2 className={`text-xl font-bold ${theme === 'dark' ? 'text-white' : 'text-slate-900'}`}>Plant Generation Overview</h2>
                            </div>
                            <div className="overflow-x-auto">
                                <table className="w-full text-left border-collapse">
                                    <thead>
                                        <tr className={`border-b ${theme === 'dark' ? 'bg-white/5 border-white/5' : 'bg-slate-100 border-slate-200'}`}>
                                            <th className="px-8 py-6 text-[10px] font-black uppercase tracking-widest text-slate-500">Plant Name</th>
                                            <th className="px-8 py-6 text-[10px] font-black uppercase tracking-widest text-slate-500">Technology</th>
                                            <th className="px-8 py-6 text-[10px] font-black uppercase tracking-widest text-slate-500">GCC Status</th>
                                            <th className="px-8 py-6 text-[10px] font-black uppercase tracking-widest text-slate-500">Issued MWh (30d)</th>
                                            <th className="px-8 py-6 text-[10px] font-black uppercase tracking-widest text-slate-500">Retired Certificates</th>
                                            <th className="px-8 py-6 text-[10px] font-black uppercase tracking-widest text-slate-500">Total Generation (MWh)</th>
                                            <th className="px-8 py-6 text-[10px] font-black uppercase tracking-widest text-slate-500 text-right">Action</th>
                                        </tr>
                                    </thead>
                                    <tbody className={`divide-y ${theme === 'dark' ? 'divide-white/5' : 'divide-slate-100'}`}>
                                        {generationOverview.plants.map(p => (
                                            <React.Fragment key={p.plant_id}>
                                                <tr className={`transition-colors cursor-pointer ${theme === 'dark' ? 'hover:bg-white/5' : 'hover:bg-slate-50'}`} onClick={() => setExpandedPlantId(expandedPlantId === p.plant_id ? null : p.plant_id)}>
                                                    <td className={`px-8 py-6 font-bold text-sm ${theme === 'dark' ? 'text-white' : 'text-slate-800'}`}>
                                                        <div className="flex items-center gap-2">
                                                            <span className="text-[10px] text-slate-500">{expandedPlantId === p.plant_id ? '▼' : '▶'}</span>
                                                            {p.plant_name}
                                                        </div>
                                                    </td>
                                                    <td className="px-8 py-6">
                                                        <span className={`text-[9px] font-bold px-2 py-1 rounded-lg ${
                                                            p.fuel_type === 'Solar' || !p.fuel_type
                                                                ? 'bg-yellow-500/10 text-yellow-400' 
                                                                : 'bg-blue-500/10 text-blue-400'
                                                        }`}>
                                                            {p.fuel_type || 'Solar'} · {p.technology_type || 'PV'}
                                                        </span>
                                                    </td>
                                                    <td className="px-8 py-6">
                                                        <span className={`text-[9px] font-bold uppercase tracking-wide ${
                                                            p.gcc_registration_status === 'approved'
                                                                ? 'text-emerald-400'
                                                                : p.gcc_registration_status === 'submitted'
                                                                ? 'text-yellow-400'
                                                                : p.gcc_registration_status === 'documents_preparing'
                                                                ? 'text-cyan-400'
                                                                : 'text-slate-500'
                                                        }`}>
                                                            {p.gcc_registration_status === 'approved' ? '✓ GCC Approved'
                                                                : p.gcc_registration_status === 'submitted' ? '⏳ GCC Review'
                                                                : p.gcc_registration_status === 'documents_preparing' ? '📋 Preparing Docs'
                                                                : 'Not Registered'}
                                                        </span>
                                                    </td>
                                                    <td className="px-8 py-6 text-emerald-500 font-bold">{Number(p.issued_mwh_30d).toFixed(2)}</td>
                                                    <td className="px-8 py-6 text-rose-400 font-bold">{p.total_retired}</td>
                                                    <td className="px-8 py-6 font-mono text-cyan-400">{Number(p.cumulative_mwh).toFixed(4)} MWh</td>
                                                    <td className="px-8 py-6 text-right" onClick={e => e.stopPropagation()}>
                                                        <Link to={`/plant/${p.plant_id}`} className="px-5 py-2 bg-cyan-500/10 hover:bg-cyan-500/20 text-cyan-400 rounded-xl text-[10px] font-black uppercase tracking-widest transition">
                                                            View Profile
                                                        </Link>
                                                    </td>
                                                </tr>
                                                {expandedPlantId === p.plant_id && (
                                                    <tr>
                                                        <td colSpan="7" className={`p-6 border-b ${theme === 'dark' ? 'bg-white/5 border-white/5' : 'bg-slate-50 border-slate-200'}`}>
                                                            <div className="space-y-4">
                                                                <h4 className={`text-xs font-black uppercase tracking-widest px-2 ${theme === 'dark' ? 'text-slate-400' : 'text-slate-600'}`}>I-REC Onboarding & Issuance Pipeline</h4>
                                                                <PipelineStatus plant={p} />
                                                            </div>
                                                        </td>
                                                    </tr>
                                                )}
                                            </React.Fragment>
                                        ))}
                                        {generationOverview.plants.length === 0 && (
                                            <tr>
                                                <td colSpan="6" className="p-20 text-center text-slate-500 font-bold italic">No plants registered on the platform.</td>
                                            </tr>
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </>
                )}
            </main>
        </div>
    );
}
